import { useState } from 'react';
import { T, useGT, useLocale } from 'gt-react';
import type {
  ExtensionColumnMetadata,
  ExtensionTargetModel,
  ExtensionValue,
} from '@erp/shared-types';
import { useExtensionDetail } from '@/api/rpc/hooks/core/useExtensionPresentation';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  EMPTY_EXTENSION_VALUE,
  formatExtensionValue,
} from './formatExtensionValue';
import { InternalUiRenderer } from './InternalUiRenderer';

export function ExtensionColumnCell({
  metadata,
  ext,
  targetModel,
  entityKey,
  currency,
}: {
  metadata?: ExtensionColumnMetadata;
  ext?: Record<string, ExtensionValue>;
  targetModel: ExtensionTargetModel;
  entityKey: string;
  currency?: string;
}) {
  const gt = useGT();
  const locale = useLocale();
  const unavailable =
    !metadata?.fieldKey ||
    !metadata.fieldType ||
    metadata.issues.some((issue) => issue.code !== 'invalid_value');
  const invalid = metadata?.issues.some(
    (issue) => issue.code === 'invalid_value' && issue.entityKey === entityKey,
  );
  if (unavailable || invalid || !metadata?.fieldKey || !metadata.fieldType) {
    return (
      <span
        className="text-muted-foreground"
        title={gt('Extension value unavailable', {
          context: 'unavailable extension column tooltip',
        })}
      >
        {EMPTY_EXTENSION_VALUE}
      </span>
    );
  }
  const value = ext?.[metadata.fieldKey];
  // A missing row or null value is plain text: there is nothing to open.
  if (value === null || value === undefined || value === '')
    return (
      <span className="text-muted-foreground">{EMPTY_EXTENSION_VALUE}</span>
    );
  const text = formatExtensionValue(value, metadata.fieldType, locale, {
    currency,
    yes: gt('Yes', { context: 'extension boolean value' }),
    no: gt('No', { context: 'extension boolean value' }),
  });
  if (!metadata.detail) return <span>{text}</span>;
  return (
    <ExtensionDetailPopover
      metadata={metadata}
      targetModel={targetModel}
      entityKey={entityKey}
      currency={currency}
      text={text}
    />
  );
}

function ExtensionDetailPopover({
  metadata,
  targetModel,
  entityKey,
  currency,
  text,
}: {
  metadata: ExtensionColumnMetadata;
  targetModel: ExtensionTargetModel;
  entityKey: string;
  currency?: string;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const gt = useGT();
  const detail = useExtensionDetail(
    targetModel,
    entityKey,
    metadata.extensionId,
    open,
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="underline decoration-dotted underline-offset-4 text-left"
          aria-label={gt('View {name} Details', {
            context: 'open extension cell detail popover',
            name: metadata.extensionName ?? metadata.label ?? metadata.fieldId,
          })}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {text}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="max-h-96 overflow-y-auto p-3"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {detail.isFetching || detail.isPending ? (
          <p role="status">
            <T context="extension detail popover loading">
              Loading extension details...
            </T>
          </p>
        ) : detail.isError ? (
          <p role="alert">
            <T context="extension detail popover read error">
              Extension details could not be loaded.
            </T>
          </p>
        ) : !detail.data?.views.length ? (
          <p>
            <T context="extension detail popover no exposed fields">
              No extension details are available.
            </T>
          </p>
        ) : (
          detail.data.views.map((view) => (
            <InternalUiRenderer
              key={view.extensionId}
              spec={view.spec}
              values={view.state.values}
              currency={currency}
            />
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}
